import { forwardRef, type ReactElement, useState } from 'react';
import { useIntl } from 'react-intl';

import { Button } from '@dynatrace/strato-components/buttons';
import { type WithChildren } from '@dynatrace/strato-components/core';
import { DotMenuIcon } from '@dynatrace/strato-icons';

import { ActionGroupContext } from './contexts/ActionGroupContext.js';
import { UseMeasureChildrenChildProps } from '../../../core/hooks/useMeasureChildren.js';
import { Menu } from '../../../navigation/menu/Menu.js';

// #region Typings
export interface CollapsedActionGroupProps extends WithChildren {
  /**
   * Icon used for the trigger that opens/closes the overlay when the ActionGroup is collapsed.
   */
  triggerIcon?: ReactElement;
  childProps?: UseMeasureChildrenChildProps;
  density?: 'default' | 'condensed';
}
// #endregion

export const CollapsedActionGroup = forwardRef<
  HTMLDivElement,
  CollapsedActionGroupProps
>((props, forwardedRef) => {
  const {
    children,
    density = 'default',
    triggerIcon = <DotMenuIcon />,
    childProps,
  } = props;
  const intl = useIntl();

  const [open, setOpen] = useState(false);

  return (
    <ActionGroupContext.Provider value={{ isCollapsed: true }}>
      <Menu open={open} onOpenChange={setOpen}>
        <Menu.Trigger>
          <Button
            variant="default"
            size={density}
            {...childProps}
            aria-label={intl.formatMessage({
              id: 'L/D6X7Ee5PYWzl3i',
              defaultMessage: 'Show more actions',
            })}
          >
            <Button.Prefix>{triggerIcon}</Button.Prefix>
          </Button>
        </Menu.Trigger>
        {open && <Menu.Content ref={forwardedRef}>{children}</Menu.Content>}
      </Menu>
    </ActionGroupContext.Provider>
  );
});
